import { FC, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { DigitalFormScopeHooks } from '@frontend/digital-forms-scope';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useAppScopeStore } from '@frontend/scope';
import {
  PopoverDialog,
  UsePopoverDialogResponse,
  SearchField,
  useFormField,
  Button,
  useDebouncedValue,
  Text,
} from '@frontend/design-system';
import LocationCheckbox from './location-checkbox/location-checkbox.component';
import {
  containerStyles,
  buttonsContainerStyles,
  locationCheckboxStyles,
  getSelectedLocationCheckboxStyles,
  searchFieldStyles,
  applyButtonStyles,
  footerStyles,
  checkboxListContainerStyles,
} from './share-org-asset.styles';

interface ShareOrgAssetProps {
  selectedLocationIds: string[];
  popoverDialogControls: UsePopoverDialogResponse;
  onApply: (locationIds: string[]) => void;
  showDefaultPicker?: boolean;
  defaultPickerTrackingId?: string;
  selectAllTrackingId?: string;
  clearAllTrackingId?: string;
  selectLocationTrackingId?: string;
  deselectLocationTrackingId?: string;
  applyButtonTrackingId?: string;
  initialLocationIds?: string[]; // Should pass a memoized list of available location IDs.
}

const ShareOrgAssetPopoverDialog: FC<ShareOrgAssetProps> = ({
  selectedLocationIds,
  popoverDialogControls,
  onApply,
  showDefaultPicker = false,
  defaultPickerTrackingId,
  selectAllTrackingId,
  clearAllTrackingId,
  selectLocationTrackingId,
  deselectLocationTrackingId,
  applyButtonTrackingId,
  initialLocationIds,
}) => {
  const { t } = useTranslation('forms');
  const searchFieldProps = useFormField({ type: 'text', placeholder: 'Search' });
  const searchTerm = useDebouncedValue(searchFieldProps.value.trim(), 300);
  const {
    isLoadingFormsEnabledOrgLocations,
    formsEnabledGLSLocationIds,
    searchFormsEnabledGLSLocationIds,
    formsEnabledLocalLocationIds,
    searchFormsEnabledLocalLocationIds,
  } = DigitalFormScopeHooks.useFormsEnabledOrgLocations({ localLocationIds: initialLocationIds });
  const [localSelectedLocationIds, setLocalSelectedLocationIds] = useState<string[]>(selectedLocationIds);
  const [availableLocationIds, setAvailableLocationIds] = useState<string[]>([]);
  const localSelectionBackup = useRef<string[]>(selectedLocationIds);
  const isInitialized = useRef(true);
  const { getScopeName } = useAppScopeStore();

  const locationIds = useMemo<string[]>(() => {
    if (!!initialLocationIds) {
      return formsEnabledLocalLocationIds;
    }

    return formsEnabledGLSLocationIds;
  }, [formsEnabledGLSLocationIds, formsEnabledLocalLocationIds, initialLocationIds]);

  const searchLocationIds = useCallback<DigitalFormScopeHooks.SearchFormsEnabledLocationIdsFn>(
    (payload) => {
      if (!!initialLocationIds) {
        return searchFormsEnabledLocalLocationIds(payload);
      }

      return searchFormsEnabledGLSLocationIds(payload);
    },
    [searchFormsEnabledGLSLocationIds, searchFormsEnabledLocalLocationIds, initialLocationIds]
  );

  /**
   * Handler function to deselect a location.
   */
  function deselectLocationHandler(locationId: string) {
    let updatedLocalSelectedLocationIds: string[] = [];

    // Remove the location ID from the selected location IDs
    setLocalSelectedLocationIds((prevSelectedLocationIds) => {
      const result = prevSelectedLocationIds.filter((selectedLocationId) => selectedLocationId !== locationId);
      updatedLocalSelectedLocationIds = result;
      return result;
    });

    // Remove the location ID from the backup
    localSelectionBackup.current = localSelectionBackup.current.filter(
      (selectedLocationId) => selectedLocationId !== locationId
    );

    // Get the available location IDs.
    const _availableLocationIds = locationIds.filter(
      (locationId) => !updatedLocalSelectedLocationIds.includes(locationId)
    );

    if (searchTerm !== '') {
      // Filter the available location IDs based on the search term.
      const filteredAvailableLocationIds = searchLocationIds({
        searchTerm,
        searchList: _availableLocationIds,
      });
      setAvailableLocationIds(filteredAvailableLocationIds);
    } else {
      setAvailableLocationIds(_availableLocationIds);
    }
  }

  /**
   * Handler function to select a location.
   */
  function selectLocationHandler(locationId: string) {
    // Update the backup selection
    localSelectionBackup.current = [...localSelectionBackup.current, locationId];

    // Remove the location ID from the available location IDs
    setAvailableLocationIds((prevAvailableLocationIds) =>
      prevAvailableLocationIds.filter((availableLocationId) => availableLocationId !== locationId)
    );

    if (searchTerm !== '') {
      // Filter the selected location IDs based on the search term.
      const filteredSelectedLocationIds = searchLocationIds({
        searchTerm,
        searchList: localSelectionBackup.current,
      });
      setLocalSelectedLocationIds(filteredSelectedLocationIds);
    } else {
      setLocalSelectedLocationIds(localSelectionBackup.current);
    }
  }

  /**
   * Handler function to select all locations.
   */
  function selectAllHandler() {
    if (searchTerm !== '') {
      // Add all the search filtered location IDs to the selected location IDs
      const filteredLocationIds = searchLocationIds({ searchTerm });
      setLocalSelectedLocationIds(filteredLocationIds);
      setAvailableLocationIds([]);

      // Update the backup selection
      for (const locationId of filteredLocationIds) {
        if (!localSelectionBackup.current.includes(locationId)) {
          localSelectionBackup.current.push(locationId);
        }
      }

      return;
    }

    setLocalSelectedLocationIds([...locationIds]);
    localSelectionBackup.current = [...locationIds];
    setAvailableLocationIds([]);
  }

  /**
   * Handler function to clear the selection.
   */
  function clearSelectionHandler() {
    if (searchTerm !== '') {
      // Clear the search filtered location IDs from the selected location IDs
      setLocalSelectedLocationIds([]);

      // Get the available location IDs based on the search term
      const filteredAvailableLocationIds = searchLocationIds({ searchTerm });
      setAvailableLocationIds(filteredAvailableLocationIds);

      // Update the backup selection
      localSelectionBackup.current = localSelectionBackup.current.filter(
        (locationId) => !filteredAvailableLocationIds.includes(locationId)
      );

      return;
    }

    // Reset the state
    setLocalSelectedLocationIds([]);
    localSelectionBackup.current = [];
    setAvailableLocationIds([...locationIds]);
  }

  /**
   * Handler function to apply the changes.
   */
  function applyChangesHandler() {
    if (localSelectedLocationIds.length === 0) {
      return;
    }

    onApply(localSelectedLocationIds);
    popoverDialogControls.close();
  }

  function getLocationPickerText() {
    if (selectedLocationIds.length === 1) {
      return getScopeName(selectedLocationIds[0]);
    }

    if (selectedLocationIds.length > 1) {
      return t('{{count}} Locations', { count: selectedLocationIds.length });
    }

    return t('Select Location');
  }

  // Initialize the available location IDs
  useEffect(() => {
    if (isInitialized.current || isLoadingFormsEnabledOrgLocations) {
      return;
    }

    const updatedAvailableLocationIds = locationIds.filter(
      (locationId) => !localSelectionBackup.current.includes(locationId)
    );
    setAvailableLocationIds(updatedAvailableLocationIds);
    isInitialized.current = true;
  }, [isLoadingFormsEnabledOrgLocations, locationIds]);

  // Update the available location IDs when the search term changes
  useEffect(() => {
    if (searchTerm === '') {
      // Restore the backup selection
      const _availableLocationIds = locationIds.filter(
        (locationId) => !localSelectionBackup.current.includes(locationId)
      );
      setAvailableLocationIds(_availableLocationIds);
      setLocalSelectedLocationIds(localSelectionBackup.current);
      return;
    }

    // Filter the selected location IDs based on the search term
    const filteredSelectedLocationIds = searchLocationIds({
      searchTerm,
      searchList: localSelectionBackup.current,
    });

    // Get the available location IDs that are not selected
    const _availableLocationIds = locationIds.filter((locationId) => {
      return !filteredSelectedLocationIds.includes(locationId);
    });

    // Filter the available location IDs based on the search term
    const fileteredAvailableLocationIds = searchLocationIds({
      searchTerm,
      searchList: _availableLocationIds,
    });
    setAvailableLocationIds(fileteredAvailableLocationIds);
    setLocalSelectedLocationIds(filteredSelectedLocationIds);
  }, [searchTerm, locationIds]);

  const numberOfSelectedLocations = localSelectedLocationIds.length;
  const numberOfAvailableLocations = availableLocationIds.length;

  return (
    <>
      {showDefaultPicker && (
        <span {...popoverDialogControls.getTriggerProps()}>
          <Button variant='secondary' iconName='location' trackingId={defaultPickerTrackingId}>
            <Text as='span' size='small'>
              {getLocationPickerText()}
            </Text>
            <Icon name={popoverDialogControls.isOpen ? 'caret-up-small' : 'caret-down-small'} />
          </Button>
        </span>
      )}

      <PopoverDialog {...popoverDialogControls.getDialogProps()} css={containerStyles}>
        <SearchField {...searchFieldProps} name='search-locations' css={searchFieldStyles} />

        <section css={buttonsContainerStyles}>
          <Button variant='tertiary' onClick={selectAllHandler} trackingId={selectAllTrackingId}>
            {t('Select All')}
          </Button>

          <Button variant='tertiary' onClick={clearSelectionHandler} trackingId={clearAllTrackingId}>
            {t('Clear')}
          </Button>
        </section>

        <section css={checkboxListContainerStyles}>
          {numberOfSelectedLocations === 0 && numberOfAvailableLocations === 0 && (
            <Text textAlign='center' color='subdued'>
              {t('Location not found!')}
            </Text>
          )}

          {numberOfSelectedLocations > 0 && (
            <div>
              {localSelectedLocationIds.map((selectedLocationId) => (
                <LocationCheckbox
                  key={selectedLocationId}
                  isSelected={true}
                  locationId={selectedLocationId}
                  onChange={deselectLocationHandler}
                  css={getSelectedLocationCheckboxStyles(numberOfAvailableLocations === 0)}
                  trackingId={deselectLocationTrackingId}
                />
              ))}
            </div>
          )}

          {numberOfAvailableLocations > 0 && (
            <div>
              {availableLocationIds.map((availableLocationId) => (
                <LocationCheckbox
                  key={availableLocationId}
                  isSelected={false}
                  locationId={availableLocationId}
                  onChange={selectLocationHandler}
                  css={locationCheckboxStyles}
                  trackingId={selectLocationTrackingId}
                />
              ))}
            </div>
          )}
        </section>

        <footer css={footerStyles}>
          <Button
            variant='primary'
            onClick={applyChangesHandler}
            css={applyButtonStyles}
            disabled={numberOfSelectedLocations === 0}
            aria-disabled={numberOfSelectedLocations === 0}
            trackingId={applyButtonTrackingId}
          >
            {t('Apply')}
          </Button>
        </footer>
      </PopoverDialog>
    </>
  );
};

export default ShareOrgAssetPopoverDialog;
